import { Component, computed, inject, input, output } from '@angular/core'
import { NgbAlertModule, NgbModal, NgbOffcanvas, NgbOffcanvasRef, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap'
import { ToastrService } from 'ngx-toastr'
import { SmartQuoteService } from 'src/app/core/services/smartquote/smartquote.service'
import { PdfModalService } from 'src/app/shared/components/pdf-modal/pdf-modal.service'
import { WolseleyStockCheckOffcanvasComponent } from 'src/app/shared/components/wolseley/wolseley-stock-check-offcanvas/wolseley-stock-check-offcanvas.component'
import { SmartQuote } from 'src/app/shared/interfaces/smartquote/smart-quote.interface'
import { SmartquoteEmailModalComponent } from '../smartquote-email-modal/smartquote-email-modal.component'
import { SmartquoteSendOrderModalComponent } from '../smartquote-send-order-modal/smartquote-send-order-modal.component'
import { DatePipe } from '@angular/common'
import { RouterLink } from '@angular/router'
import { NgxPermissionsModule } from 'ngx-permissions'
import { ButtonComponent } from 'src/app/shared/components/form-elements/button/button.component'
import { PageTitleComponent } from 'src/app/shared/components/page-title/page-title.component'
import { SmartquoteHelperService } from '../../../services/smartquote-helper.service'

@Component({
    selector: 'sf-smartquote-top-bar-2',
    imports: [
        RouterLink,
        NgbAlertModule,
        NgbTooltipModule,
        NgxPermissionsModule,
        PageTitleComponent,
        ButtonComponent,
        DatePipe,
    ],
    templateUrl: './smartquote-top-bar-2.component.html',
    styleUrl: './smartquote-top-bar-2.component.scss',
    providers: [DatePipe],
})
export class SmartquoteTopBar2Component {
    #smartquoteService = inject(SmartQuoteService)
    #modalService = inject(NgbModal)
    #offcanvas = inject(NgbOffcanvas)
    #toastr = inject(ToastrService)
    #pdfModalService = inject(PdfModalService)
    #datePipe = inject(DatePipe)

    wolseleyStockCheckOffcanvas: NgbOffcanvasRef | null = null

    smartquoteHelperService = inject(SmartquoteHelperService)
    smartquote = input<SmartQuote | null>()
    statistics = this.smartquoteHelperService.statistics()
    email = input<string | null>()
    landingPageUrl = input<string | null>()
    totalCost = input<number | null>()
    totalExVatCost = input<number | null>()
    deposit = input<number | null>()
    canOrderFromWolseley = input<boolean | null>()
    productCodes = input<string[]>([])

    onAccepted = output()
    onDeclined = output()
    onCopy = output()
    onDeleted = output()

    smartQuoteStatus = computed(() => {
        const quote = this.smartquote()
        let message = ''

        if (quote?.accepted) {
            message = `Quote Accepted`
            if (quote.acceptedByUser) {
                message += ` by ${quote.acceptedByUser.fullName}`
            }
            if (quote.acceptedByCustomer) {
                message += ` by Customer`
            }
            if (quote.acceptedDate) {
                message += ` on ${this.#datePipe.transform(quote.acceptedDate, 'dd/MM/yyyy HH:mm a')}`
            }
        } else if (quote?.declined) {
            message = `Quote Declined`
            if (quote.declinedByUser) {
                message += ` by ${quote.declinedByUser.fullName}`
            }
            if (quote.declinedByCustomer) {
                message += ` by Customer`
            }
            if (quote.declinedDate) {
                message += ` on ${this.#datePipe.transform(quote.declinedDate, 'dd/MM/yyyy HH:mm a')}`
            }
        }
        return message
    })

    openPDFModal(): void {
        this.#smartquoteService.downloadPdf(this.smartquote()!.id).subscribe({
            next: (response) => {
                this.#pdfModalService.openModal(response, 'SmartQuote')
            },
        })
    }

    openEmailModal(): void {
        const ref = this.#modalService.open(SmartquoteEmailModalComponent, { size: 'xl', fullscreen: 'lg' })
        ref.componentInstance.smartQuoteId = this.smartquote()!.id
        ref.componentInstance.emailAddress = this.email()
        ref.componentInstance.smartQuote = this.smartquote()
    }

    openSendOrderModal(): void {
        if (this.smartquote()!.lineItems.length === 0) {
            this.#toastr.warning('Please add some line items to the Smart Quote before sending an order.')
        }
        const ref = this.#modalService.open(SmartquoteSendOrderModalComponent, { size: 'xl' })
        ref.componentInstance.smartQuoteId = this.smartquote()!.id
    }

    openWolseleyStockCheck(): void {
        const productCodes = this.productCodes()

        if (productCodes.length === 0) {
            this.#toastr.warning('Please add some products (with catalog numbers) to check stock for')
            return
        }

        this.wolseleyStockCheckOffcanvas = this.#offcanvas.open(WolseleyStockCheckOffcanvasComponent, {
            position: 'end',
        })
        this.wolseleyStockCheckOffcanvas.componentInstance.productCodes = productCodes
    }
}
